import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, KeyRound, ArrowLeft, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useTwoFactorAuthStore } from '@/store/twoFactorAuth';
import { useTwoFactor } from '@/hooks/useTwoFactor';
import { supabase } from '@/lib/supabase';

export default function TwoFactorVerify() {
  const navigate = useNavigate();
  const { pendingAuth, clearPendingAuth } = useTwoFactorAuthStore();
  const { verifyCode, verifyBackupCode } = useTwoFactor();

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trustDevice, setTrustDevice] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, [useBackupCode]);

  // Redirect if no pending auth
  useEffect(() => {
    if (!pendingAuth) {
      navigate('/login');
    }
  }, [pendingAuth, navigate]);

  // Auto-submit when 6 digits entered (for TOTP only)
  useEffect(() => {
    if (!useBackupCode && code.length === 6) {
      handleVerify();
    }
  }, [code, useBackupCode]);

  const handleVerify = async () => {
    if (!pendingAuth) return;

    const codeToVerify = code.trim();
    if (useBackupCode) {
      if (codeToVerify.length < 8) {
        setError('Please enter a valid backup code');
        return;
      }
    } else {
      if (codeToVerify.length !== 6) {
        setError('Please enter a 6-digit code');
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      // Verify the code - user is still authenticated, pass IDs from pendingAuth
      const isValid = useBackupCode
        ? await verifyBackupCode(codeToVerify, pendingAuth.userId, pendingAuth.organizationId)
        : await verifyCode(codeToVerify, pendingAuth.userId, pendingAuth.organizationId);

      if (!isValid) {
        setError(useBackupCode ? 'Invalid backup code' : 'Invalid verification code');
        setIsLoading(false);
        setCode('');
        inputRef.current?.focus();
        return;
      }

      // Get session token for device tracking
      const sessionToken = localStorage.getItem('blazepos_session_token') || '';

      // Record the verification
      await supabase.rpc('record_2fa_verification', {
        p_user_id: pendingAuth.userId,
        p_organization_id: pendingAuth.organizationId,
        p_device_token: sessionToken,
        p_trust_device: trustDevice && pendingAuth.triggerReason === 'new_device',
      });

      // Clear pending auth - user is already authenticated, just allow access now
      clearPendingAuth();
      navigate('/dashboard');
    } catch (err) {
      console.error('2FA verification error:', err);
      setError('Verification failed. Please try again.');
      setIsLoading(false);
      setCode('');
      inputRef.current?.focus();
    }
  };

  const handleCancel = async () => {
    clearPendingAuth();
    // Sign out since user chose not to complete 2FA
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (useBackupCode) {
      // Allow alphanumeric with dashes for backup codes
      setCode(value.toUpperCase());
    } else {
      // Only allow digits for TOTP
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 6) {
        setCode(digits);
      }
    }
    setError(null);
  };

  const getTriggerMessage = () => {
    switch (pendingAuth?.triggerReason) {
      case 'new_device':
        return "We don't recognize this device. Please verify your identity.";
      case 'inactivity':
        return "Your session has been inactive. Please verify your identity.";
      case 'every_login':
      default:
        return 'Enter the code from your authenticator app.';
    }
  };

  if (!pendingAuth) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-zinc-100 dark:bg-zinc-950">
      {/* Header Bar */}
      <header className="bg-zinc-950 dark:bg-black text-white px-6 py-3 flex items-center justify-between border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="text-zinc-300 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Login
        </Button>
      </header>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white dark:bg-zinc-900 rounded-xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-white dark:bg-zinc-800 flex items-center justify-center mb-4 shadow-sm border border-zinc-200 dark:border-zinc-700">
              <img src="/blazepos-logo.png" alt="BlazePOS" className="w-10 h-10 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
              Two-Factor Authentication
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm">
              {getTriggerMessage()}
            </p>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="code" className="flex items-center gap-2">
                {useBackupCode ? (
                  <>
                    <KeyRound className="h-4 w-4" />
                    Backup Code
                  </>
                ) : (
                  <>
                    <Smartphone className="h-4 w-4" />
                    Verification Code
                  </>
                )}
              </Label>
              <Input
                ref={inputRef}
                id="code"
                type="text"
                inputMode={useBackupCode ? 'text' : 'numeric'}
                value={code}
                onChange={handleCodeChange}
                placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
                className={`text-center text-2xl tracking-widest font-mono ${
                  error ? 'border-red-500' : ''
                }`}
                disabled={isLoading}
                autoComplete="one-time-code"
              />
              {error && <p className="text-sm text-red-500 text-center">{error}</p>}
            </div>

            {pendingAuth.triggerReason === 'new_device' && !useBackupCode && (
              <div className="flex items-center gap-3 p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                <Checkbox
                  id="trust-device"
                  checked={trustDevice}
                  onCheckedChange={(checked) => setTrustDevice(checked === true)}
                  disabled={isLoading}
                />
                <label
                  htmlFor="trust-device"
                  className="text-sm text-zinc-600 dark:text-zinc-300 cursor-pointer"
                >
                  Trust this device for future logins
                </label>
              </div>
            )}

            <Button
              onClick={handleVerify}
              disabled={isLoading || (useBackupCode ? code.length < 8 : code.length !== 6)}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setCode('');
                  setError(null);
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                disabled={isLoading}
              >
                {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
